import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared chrome for the Proxy Vault surfaces.
 *
 * Kept in one file because the section workspace and each connection workspace
 * are the same shape — a header, a tab strip, cards, a table — and duplicating
 * that shape twice is how two pages that should look identical stop looking
 * identical.
 */

export const mono = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export function Tabs({
  tabs,
  active,
  onChange,
  right,
}: {
  tabs: Array<{ id: string; label: string }>;
  active: string;
  onChange: (id: string) => void;
  right?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg)',
      }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          data-testid={`pv-tab-${tab.id}`}
          onClick={() => onChange(tab.id)}
          style={{
            padding: '10px 14px',
            fontSize: 12,
            fontWeight: active === tab.id ? 600 : 400,
            color: active === tab.id ? 'var(--text)' : 'var(--text-soft)',
            background: 'transparent',
            border: 0,
            borderBottom: `2px solid ${active === tab.id ? 'var(--accent)' : 'transparent'}`,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {tab.label}
        </button>
      ))}
      <div style={{ marginLeft: 'auto' }}>{right}</div>
    </div>
  );
}

export function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 14,
        background: 'var(--surface)',
        minWidth: 190,
        flex: '1 1 190px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--text-mute)',
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

export function Metric({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{value}</span>{' '}
      <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>{label}</span>
    </div>
  );
}

const STATUS_COLOR: Record<string, string> = {
  active: '#2f855a',
  incomplete: 'var(--accent)',
  revoked: 'var(--text-mute)',
  forwarded: '#2f855a',
  refused: '#c0392b',
};

export function StatusDot({ status, label }: { status: string; label?: ReactNode }) {
  const color = STATUS_COLOR[status] ?? 'var(--text-soft)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color,
        // A supplied label is already worded; the bare status is an enum value.
        textTransform: label === undefined ? 'capitalize' : 'none',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {label ?? status}
    </span>
  );
}

/**
 * "1 main · 4 children" — who has this connection fully bound. `main` is the
 * connection's own organization (0 or 1), `children` its descendants. Null when
 * the roll-up would say nothing: a revoked connection, or a private one that no
 * descendant could have bound anyway.
 */
export function bindingRollUp(c: {
  status: string;
  visibility: string;
  bound_organizations: number;
  bound_descendants: number;
}): { main: number; children: number; text: string } | null {
  if (c.status === 'revoked') return null;
  const children = c.bound_descendants;
  if (c.visibility !== 'children' && children === 0) return null;
  const main = Math.max(0, c.bound_organizations - children);
  return { main, children, text: `${main} main · ${children} ${children === 1 ? 'child' : 'children'}` };
}

export function Pill({ children, tone }: { children: ReactNode; tone?: 'warn' | 'mute' }) {
  return (
    <span
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: tone === 'warn' ? '#b7791f' : 'var(--text-soft)',
        background: tone === 'warn' ? 'rgba(183,121,31,0.12)' : 'var(--bg-alt)',
        padding: '2px 7px',
        borderRadius: 999,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

export const theadRowStyle: CSSProperties = {
  background: 'var(--bg-alt)',
  textAlign: 'left',
  color: 'var(--text-soft)',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

export function Th({ children, first }: { children: ReactNode; first?: boolean }) {
  return <th style={{ padding: first ? '10px 20px' : '10px', fontWeight: 600 }}>{children}</th>;
}

export function Td({
  children,
  first,
  isMono,
}: {
  children: ReactNode;
  first?: boolean;
  isMono?: boolean;
}) {
  return (
    <td
      style={{
        padding: first ? '10px 20px' : '10px',
        color: 'var(--text)',
        fontFamily: isMono ? mono : 'inherit',
        fontSize: isMono ? 11 : 12,
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

export const inputStyle: CSSProperties = {
  padding: '5px 10px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
};

export const selectStyle: CSSProperties = { ...inputStyle, cursor: 'pointer' };

export const primaryButton: CSSProperties = {
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 500,
  background: 'var(--accent)',
  color: '#fff',
  border: 0,
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const secondaryButton: CSSProperties = {
  ...primaryButton,
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
};

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{ padding: 40, fontSize: 12, color: 'var(--text-mute)', textAlign: 'center' }}>
      {children}
    </div>
  );
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffHours = (Date.now() - d.getTime()) / 3_600_000;
  if (diffHours < 24) return d.toLocaleTimeString(undefined, { hour12: false });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)}K`;
  return `${(n / (1024 * 1024)).toFixed(1)}M`;
}

/** ▲ reveal · ▼ redact · ⨯ refused · · passthrough — the legend the wireframe uses. */
export function OperationGlyphs({
  operations,
  verdict,
}: {
  operations: Array<{ verb: string }>;
  verdict: string;
}) {
  if (verdict === 'refused') return <span style={{ color: '#c0392b' }}>⨯</span>;
  if (operations.length === 0) return <span style={{ color: 'var(--text-mute)' }}>·</span>;
  const reveals = operations.filter((o) => o.verb === 'reveal').length;
  const redacts = operations.filter((o) => o.verb === 'redact').length;
  return (
    <span style={{ color: 'var(--text-soft)' }}>
      {reveals > 0 && <span style={{ color: '#2f855a' }}>▲</span>}
      {redacts > 0 && <span style={{ color: '#2b6cb0' }}>▼</span>}
    </span>
  );
}

/**
 * What to call a connection on screen.
 *
 * `name` is the identifier — immutable, unique per organization, and the thing
 * `export` emits — so it cannot carry spaces or capitals or a vendor's actual
 * product name. `display_name` is the label an operator wrote for themselves.
 * Everywhere a human reads a connection, they read this.
 */
export function connectionLabel(c: { name: string; display_name?: string | null }): string {
  return c.display_name?.trim() || c.name;
}
